import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { TreatmentsSectionDTO } from '@lib/strapi/dtos/components/sections/treatmentsSection.dto';
import { SectionHeading, dataOf, blobFor } from '../shared-ui';
import { treatmentIconFor, IconWaves } from '@components/utils/icons';

/**
 * Treatment cards — a GRID with a second row, deliberately not a carousel
 * (D4 review): a carousel hides most of its content from users and crawlers
 * alike. Cards link to the root-level /[slug] pages (D18).
 */
type TreatmentCard = { slug?: string; title?: string; teaser?: string; coverImage?: { url: string; alternativeText?: string } };

const TreatmentsSectionImplementation = ({ section, page }: ComponentProps<TreatmentsSectionDTO>) => {
  const { t } = useTranslation('main');
  if (!section) return null;
  const data = dataOf(page);
  const all = data.treatments as TreatmentCard[] | undefined;
  // explicit slugs win: the editor's comma-separated list names WHICH
  // treatments appear AND in what order (owner ask, 2026-09-08)
  const picked = section.slugs
    ? section.slugs.split(',').map((s) => s.trim()).filter(Boolean)
        .map((slug) => (all ?? []).find((t) => t.slug === slug))
        .filter((t): t is TreatmentCard => Boolean(t))
    : null;
  const items = picked ?? ((section.source === 'HomepageCarousel' ? data.homepageTreatments : all) as
    | TreatmentCard[]
    | undefined);
  if (!items?.length) return null;
  const showAll = Boolean(picked) || section.source === 'HomepageCarousel';
  // Designer pass 2026-09-14 (strip 2.psd): the HOMEPAGE band sits on a
  // full-bleed violet-050 field with circular thumbs and the stamp-ring
  // watermark clipping the right edge; listing pages keep the blob grid.
  const isBand = section.source === 'HomepageCarousel';

  // With no section heading there is no h2 between the page h1 and the card
  // titles — heading-order violation (Lighthouse a11y, 2026-09-16; same rule
  // as StepsSection). Card titles take the level that follows what exists.
  const CardTitle = section.heading ? 'h3' : 'h2';
  return (
    <section className={isBand
      ? "bleed relative my-14 bg-violet-050 py-14 [overflow-x:clip] md:my-20 md:py-20"
      : "my-14 md:my-20"}>
      {isBand ? (
        // overflow-x clip (not hidden) lets the stamp POKE ABOVE the band's
        // top edge into the section before it, per the PSD, while the page
        // still cannot scroll sideways
        <img loading="lazy" src="/images/brand/stamp-mono.svg" alt="" aria-hidden="true"
          className="pointer-events-none absolute -right-8 -top-14 w-[16rem] rotate-12 select-none opacity-25 md:right-16 md:-top-20 md:w-[20rem]" />
      ) : null}
      <div className={isBand ? "shell relative" : ""}>
      <SectionHeading heading={section.heading} intro={section.intro} icon={<IconWaves />} />
      <div className="grid grid-cols-1 gap-x-5 gap-y-8 min-[360px]:grid-cols-2 sm:grid-cols-3 lg:grid-cols-4">
        {items.map((t, i) => {
          const Icon = treatmentIconFor(t.slug);
          return (
          <Link key={t.slug ?? i} href={`/${t.slug}`} className="group">
            <div className={isBand
              ? "relative mx-auto aspect-square w-full max-w-[13rem] overflow-hidden rounded-full bg-white transition-transform group-hover:-translate-y-1"
              : `${blobFor(i)} relative aspect-[4/3] overflow-hidden bg-violet-050 transition-transform group-hover:-translate-y-1`}>
              {t.coverImage ? (
                <Image src={t.coverImage.url} alt={t.coverImage.alternativeText ?? t.title ?? ''} fill
                  sizes="(min-width:1024px) 16rem, (min-width:360px) 45vw, 90vw" className="clinical-photo" />
              ) : null}
            </div>
            <CardTitle className="mt-3 flex items-start gap-1.5 text-lg font-extrabold leading-snug text-ink group-hover:text-violet-700">
              {/* modality icon (approved icon set): informational — the same
                  family the treatment detail page uses, so the card and its
                  destination read as one thing */}
              {Icon ? <Icon className="mt-0.5 flex-none text-violet-700" /> : null}
              <span>{t.title}</span>
            </CardTitle>
            {t.teaser ? <p className="mt-1 line-clamp-2 text-[0.9375rem] text-muted">{t.teaser}</p> : null}
          </Link>
          );
        })}
      </div>
      {showAll ? (
        <div className="mt-8">
          <Link href="/therapeies"
            className="inline-flex items-center gap-1.5 rounded-full border-2 border-violet-700 px-5 py-2.5 text-sm font-extrabold text-violet-700 transition-colors hover:bg-violet-700 hover:text-white">
            {t('treatments.viewAll')}
          </Link>
        </div>
      ) : null}
      </div>
    </section>
  );
};

export default TreatmentsSectionImplementation;
